import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import { formatApiError } from "../api/client";
import { trackFunnel } from "../lib/funnel";
import {
  ReferralInviteHero,
  ReferralSuccessOverlay,
} from "../components/referral/ReferralWelcome";

// Phase H2-followup #2 — sessionStorage key for the captured `?ref=CODE`
// so the referral attribution survives Login ↔ Register navigation and
// any incidental reloads that drop the URL param.
const REF_SESSION_KEY = "dealalert_pending_ref";

export default function RegisterPage() {
  const { t } = useTranslation("auth");
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get("next") || "/dashboard";

  // ----- Referral capture -------------------------------------------------
  // Sprint R2 Block 4 — `refCode` is now editable: pre-filled from
  // `?ref=...` URL param or sessionStorage, but the user can manually
  // type a friend's code if they didn't land via the share link.
  const [refCode, setRefCode] = useState(() => {
    const fromUrl = (searchParams.get("ref") || "").trim().toUpperCase();
    if (fromUrl) {
      try {
        sessionStorage.setItem(REF_SESSION_KEY, fromUrl);
      } catch {
        /* sessionStorage disabled (e.g., privacy mode) — ignore */
      }
      return fromUrl;
    }
    try {
      return (sessionStorage.getItem(REF_SESSION_KEY) || "").trim().toUpperCase();
    } catch {
      return "";
    }
  });

  // P1 — declared before the navigation effect (which reads `celebration`).
  const [celebration, setCelebration] = useState(false);
  const firstNameRef = useRef(null);
  // Synchronous nav-hold: set BEFORE awaiting register() so the freshly
  // persisted `user` cannot trigger the navigation effect before the
  // referral celebration mounts. A `celebration` STATE set after the await
  // loses the race against the setUser commit inside AuthContext.persist().
  const holdNavRef = useRef(false);

  useEffect(() => {
    trackFunnel("registration_started");
  }, []);

  useEffect(() => {
    // The referral celebration holds navigation via a synchronous ref;
    // the overlay CTA releases it and navigates explicitly.
    if (holdNavRef.current) return;
    if (user) {
      // P0 2026-07-15 — unverifizierte User gehören auf die Wall, nicht
      // auf nextPath (Race: register() setzt user → dieser Effect lief
      // gegen die navigate("/verify-pending") im Submit-Handler).
      if (user.email_verified === false) {
        navigate("/verify-pending", { state: { email: user.email }, replace: true });
      } else {
        navigate(nextPath, { replace: true });
      }
    }
  }, [user, nextPath, navigate]);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!termsAccepted || !privacyAccepted) {
      setError(t("register.legal_acceptance_required"));
      return;
    }
    setSubmitting(true);
    try {
      // Hold navigation synchronously in case the referral applies — set
      // BEFORE the await so the persisted user can't navigate us away.
      if (refCode) holdNavRef.current = true;
      const res = await register({
        email: email.trim().toLowerCase(),
        password,
        first_name: firstName.trim() || null,
        last_name: lastName.trim() || null,
        phone: phone.trim() || null,
        terms_accepted: true,
        privacy_accepted: true,
        marketing_opt_in: marketingOptIn,
        referral_code: refCode || null,
      });
      try {
        sessionStorage.removeItem(REF_SESSION_KEY);
      } catch {
        /* noop */
      }
      if (res.appliedReferral) {
        // Backend granted the trial → show the ONE welcome moment. The
        // overlay CTA releases the hold and navigates.
        setCelebration(true);
        return;
      }
      // Not applied (no/invalid code, ineligible) → release + navigate.
      holdNavRef.current = false;
      if (res.emailVerified === false) {
        navigate("/verify-pending", { state: { email: email.trim().toLowerCase() } });
      } else {
        navigate(nextPath, { replace: true });
      }
    } catch (err) {
      holdNavRef.current = false;
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-md space-y-6" data-testid="register-page">
      {refCode ? (
        <ReferralInviteHero
          t={t}
          refCode={refCode}
          onCta={() => {
            firstNameRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            setTimeout(() => firstNameRef.current?.focus(), 350);
          }}
        />
      ) : (
        <header className="space-y-2">
          <h1 className="text-3xl font-semibold tracking-tight">{t("register.heading")}</h1>
          <p className="text-sm text-zinc-400">
            {t("register.have_account")}{" "}
            <Link to="/login" className="text-emerald-400 underline-offset-4 hover:underline" data-testid="link-to-login">
              {t("register.login_link")}
            </Link>
            .
          </p>
        </header>
      )}

      <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label htmlFor="reg-first" className="text-xs uppercase tracking-[0.18em] text-zinc-500">
              {t("register.first_name")}
            </label>
            <input
              id="reg-first"
              type="text"
              required
              autoComplete="given-name"
              ref={firstNameRef}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              data-testid="register-first-name-input"
              className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-500 focus:border-emerald-500 focus:outline-none"
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="reg-last" className="text-xs uppercase tracking-[0.18em] text-zinc-500">
              {t("register.last_name")}
            </label>
            <input
              id="reg-last"
              type="text"
              required
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              data-testid="register-last-name-input"
              className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-500 focus:border-emerald-500 focus:outline-none"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <label htmlFor="reg-email" className="text-xs uppercase tracking-[0.18em] text-zinc-500">
            {t("register.email")}
          </label>
          <input
            id="reg-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="register-email-input"
            className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-500 focus:border-emerald-500 focus:outline-none"
          />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="reg-phone" className="text-xs uppercase tracking-[0.18em] text-zinc-500">
            {t("register.phone")} <span className="text-zinc-600 normal-case tracking-normal">{t("register.optional")}</span>
          </label>
          <input
            id="reg-phone"
            type="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            data-testid="register-phone-input"
            placeholder="+43 …"
            className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-500 focus:border-emerald-500 focus:outline-none"
          />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="reg-password" className="text-xs uppercase tracking-[0.18em] text-zinc-500">
            {t("register.password")} <span className="text-zinc-600 normal-case tracking-normal">{t("register.password_hint")}</span>
          </label>
          <input
            id="reg-password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="register-password-input"
            className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-500 focus:border-emerald-500 focus:outline-none"
          />
        </div>

        {/* Sprint R2 Block 4 — optional referral-code input. Pre-filled
            from `?ref=…` URL param if present, but always editable so a
            user who heard about a friend's code by mouth can type it
            here at signup. Same `referral_code` field on the API. */}
        <div className="space-y-1.5">
          <label htmlFor="reg-referral" className="text-xs uppercase tracking-[0.18em] text-zinc-500">
            {t("register.referral_code_label")} <span className="text-zinc-600 normal-case tracking-normal">{t("register.optional")}</span>
          </label>
          <input
            id="reg-referral"
            type="text"
            autoComplete="off"
            value={refCode}
            onChange={(e) => setRefCode((e.target.value || "").toUpperCase().slice(0, 32))}
            data-testid="register-referral-input"
            placeholder={t("register.referral_code_placeholder")}
            className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-500 focus:border-emerald-500 focus:outline-none font-mono uppercase tracking-wider"
          />
          <p className="text-[11px] text-zinc-500 leading-relaxed">
            {t("register.referral_code_helper")}
          </p>
        </div>

        <div className="space-y-3 pt-2 border-t border-zinc-900">
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              onInvalid={(e) =>
                e.currentTarget.setCustomValidity(t("register.terms_required_html"))
              }
              onInput={(e) => e.currentTarget.setCustomValidity("")}
              data-testid="register-terms-checkbox"
              className="mt-0.5 h-4 w-4 rounded border-zinc-700 bg-zinc-900 text-emerald-500 focus:ring-emerald-500"
              required
            />
            <span className="text-xs text-zinc-400 leading-relaxed">
              {t("register.terms_accept_prefix")}{" "}
              <Link to="/agb" target="_blank" className="text-emerald-400 hover:underline">
                {t("register.terms_link")}
              </Link>
              . *
            </span>
          </label>
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={privacyAccepted}
              onChange={(e) => setPrivacyAccepted(e.target.checked)}
              onInvalid={(e) =>
                e.currentTarget.setCustomValidity(t("register.privacy_required_html"))
              }
              onInput={(e) => e.currentTarget.setCustomValidity("")}
              data-testid="register-privacy-checkbox"
              className="mt-0.5 h-4 w-4 rounded border-zinc-700 bg-zinc-900 text-emerald-500 focus:ring-emerald-500"
              required
            />
            <span className="text-xs text-zinc-400 leading-relaxed">
              {t("register.privacy_accept_prefix")}{" "}
              <Link to="/datenschutz" target="_blank" className="text-emerald-400 hover:underline">
                {t("register.privacy_link")}
              </Link>{" "}
              {t("register.privacy_accept_suffix")} *
            </span>
          </label>
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={marketingOptIn}
              onChange={(e) => setMarketingOptIn(e.target.checked)}
              data-testid="register-marketing-checkbox"
              className="mt-0.5 h-4 w-4 rounded border-zinc-700 bg-zinc-900 text-emerald-500 focus:ring-emerald-500"
            />
            <span className="text-xs text-zinc-500 leading-relaxed">
              {t("register.marketing_text")}
            </span>
          </label>
        </div>

        {error && (
          <div
            data-testid="register-error"
            className="rounded-md border border-rose-900 bg-rose-950/40 px-3 py-2 text-sm text-rose-300"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          data-testid="register-submit"
          className="w-full rounded-md bg-emerald-500 px-4 py-2.5 text-sm font-medium text-zinc-950 transition-colors hover:bg-emerald-400 disabled:opacity-60"
        >
          {submitting ? t("register.submitting") : t("register.submit")}
        </button>
      </form>

      {refCode && (
        <p
          className="text-center text-xs text-zinc-500"
          data-testid="register-existing-member"
        >
          {t("register.have_account")}{" "}
          <Link
            to="/login"
            className="text-emerald-400 underline-offset-4 hover:underline"
            data-testid="link-to-login"
          >
            {t("register.login_link")}
          </Link>
        </p>
      )}

      <ReferralSuccessOverlay
        t={t}
        open={celebration}
        onClose={() => {
          holdNavRef.current = false;
          setCelebration(false);
          if (user?.email_verified === false) {
            navigate("/verify-pending", {
              state: { email: user.email },
              replace: true,
            });
          } else {
            navigate(nextPath, { replace: true });
          }
        }}
      />
    </div>
  );
}
